// Phase E (Jul-2026) — Project Reporting Period historical backfill.
// 4-stage safe migration: Dry Run → Apply (high-confidence only) →
// Worklist → Verification. Admin-only, reversible by Run ID.
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui-helpers";
import {
  CalendarClock, FileDown, Loader2, PlayCircle, RotateCcw,
  ShieldCheck, Undo2,
} from "lucide-react";

const btn = "inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border disabled:opacity-50";
const btnGhost = `${btn} border-slate-300 text-slate-700 hover:bg-slate-50`;
const btnOrange = `${btn} border-[#F97316] bg-[#F97316] text-white hover:bg-[#EA580C]`;
const btnRose = `${btn} border-rose-600 text-rose-700 hover:bg-rose-50`;

async function downloadBlob(url, fallbackName) {
  const r = await api.get(url, { responseType: "blob", timeout: 300000 });
  const dispo = r.headers["content-disposition"] || "";
  const m = dispo.match(/filename=([^;]+)/);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = m ? m[1].trim() : fallbackName;
  a.click();
  URL.revokeObjectURL(a.href);
}

function Check({ ok, label }) {
  return (
    <div className={`flex items-center gap-1.5 text-[11px] font-bold ${ok ? "text-emerald-700" : "text-rose-700"}`}>
      <span>{ok ? "✓" : "✗"}</span>{label}
    </div>
  );
}

function RowsTable({ rows, cap = 8 }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-2 max-h-56 overflow-auto border border-slate-200">
      <table className="w-full text-[10px]">
        <thead className="bg-slate-100 sticky top-0">
          <tr>
            {["Module", "Voucher", "Ln", "Acct Period", "Proposed", "Basis", "Conf"].map(h =>
              <th key={h} className="text-left px-1.5 py-1 font-bold uppercase text-slate-500">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, cap).map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-1.5 py-1">{r.module}</td>
              <td className="px-1.5 py-1 font-mono">{r.voucher_number || r.doc_id?.slice(0, 8)}</td>
              <td className="px-1.5 py-1">{r.line_index === null || r.line_index === undefined ? "" : r.line_index + 1}</td>
              <td className="px-1.5 py-1">{r.accounting_period}</td>
              <td className="px-1.5 py-1 font-bold">{r.proposed || "—"}</td>
              <td className="px-1.5 py-1 text-slate-500">{r.basis}</td>
              <td className="px-1.5 py-1">{r.confidence}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > cap && (
        <div className="px-1.5 py-1 text-[10px] text-slate-400 bg-slate-50">
          … {rows.length - cap} more row(s) — use the Excel export for the full list
        </div>
      )}
    </div>
  );
}

export function ReportingPeriodMigrationTile() {
  const [busy, setBusy] = useState("");
  const [dry, setDry] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [applyResult, setApplyResult] = useState(null);
  const [wl, setWl] = useState(null);
  const [runs, setRuns] = useState(null);
  const [verifyRes, setVerifyRes] = useState(null);

  async function run(name, fn) {
    setBusy(name);
    try { await fn(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${name} failed`); }
    finally { setBusy(""); }
  }

  const dryRun = () => run("dry", async () => {
    const r = await api.get("/admin/reporting-period-migration/dry-run", { timeout: 300000 });
    setDry(r.data);
    setApplyResult(null);
    toast.success(`Dry run complete — ${r.data.totals.auto} high-confidence, ${r.data.totals.worklist} for worklist`);
  });

  const apply = () => run("apply", async () => {
    const r = await api.post("/admin/reporting-period-migration/apply",
      { confirm: "MIGRATE", expected_auto_count: dry.totals.auto }, { timeout: 600000 });
    setApplyResult(r.data);
    setConfirm("");
    r.data.verification?.all_passed
      ? toast.success(`Migration complete — ${r.data.counts.migrated} record(s) migrated, all verification checks PASSED`)
      : toast.error("Migration finished but a verification check FAILED — review before proceeding");
  });

  const loadWorklist = () => run("wl", async () => {
    const r = await api.get("/admin/reporting-period-migration/worklist", { timeout: 300000 });
    setWl(r.data);
  });

  const loadRuns = () => run("runs", async () => {
    const r = await api.get("/admin/reporting-period-migration/runs");
    setRuns(r.data.runs || []);
  });

  const reverify = () => run("verify", async () => {
    const r = await api.get("/admin/reporting-period-migration/verify", { timeout: 300000 });
    setVerifyRes(r.data);
    r.data.ok ? toast.success("Re-verification PASSED") : toast.error("Re-verification found issues");
  });

  const rollback = (runId) => {
    const typed = window.prompt("This restores every record migrated in this run from its backup.\nType ROLLBACK to confirm:");
    if (typed !== "ROLLBACK") { if (typed !== null) toast.error("Rollback not confirmed"); return; }
    run("rollback", async () => {
      const r = await api.post("/admin/reporting-period-migration/rollback",
        { run_id: runId, confirm: "ROLLBACK" }, { timeout: 600000 });
      toast.success(`Rolled back — ${r.data.restored} record(s) restored (reconcile ${r.data.reconcile_ok ? "OK" : "FAILED"})`);
      await loadRuns();
    });
  };

  const t = dry?.totals;
  const autoRows = (dry?.rows || []).filter(r => r.action === "auto");

  return (
    <Card className="p-5" data-testid="rp-migration-tile">
      <div className="flex items-start gap-2 mb-1">
        <CalendarClock size={18} className="text-[#F97316] mt-0.5 shrink-0" />
        <div>
          <h3 className="font-display text-sm font-bold text-slate-900">Project Reporting Period — Historical Backfill (Phase E)</h3>
          <p className="text-[11px] text-slate-500 leading-snug">
            One-time, reversible migration that stamps the management-only reporting month on historical
            vouchers. Accounting dates, GST, ledgers and the frozen Project P&amp;L are never touched — verified
            automatically on every Apply.
          </p>
        </div>
      </div>

      {/* Stage 1 — Dry Run */}
      <div className="border border-slate-200 p-3 mt-3" data-testid="rp-mig-stage1">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Stage 1 · Dry Run (no writes)</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={dryRun} disabled={!!busy} className={btnOrange} data-testid="rp-mig-dryrun-btn">
            {busy === "dry" ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />} Run Dry Run
          </button>
          <button onClick={() => downloadBlob("/admin/reporting-period-migration/export.xlsx?kind=dry-run", "dry-run.xlsx")}
            disabled={!!busy} className={btnGhost} data-testid="rp-mig-dryrun-export-btn">
            <FileDown size={12} /> Dry-Run Excel
          </button>
        </div>
        {t && (
          <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-1.5 text-center" data-testid="rp-mig-dryrun-summary">
            {[["Auto (HIGH)", t.auto, "text-emerald-700"], ["Worklist", t.worklist, "text-amber-700"],
              ["Inherit (dynamic)", t.inherit, "text-sky-700"], ["Already set", t["already-set"], "text-slate-600"],
              ["Fallback (assumed)", t.fallback, "text-slate-500"], ["Excluded", t.excluded, "text-slate-400"]].map(([l, v, cls]) => (
              <div key={l} className="border border-slate-200 py-1.5">
                <div className={`text-base font-bold tabular ${cls}`}>{v}</div>
                <div className="text-[9px] uppercase text-slate-400 leading-tight">{l}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stage 2 — Apply */}
      <div className="border border-slate-200 p-3 mt-2" data-testid="rp-mig-stage2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Stage 2 · Apply high-confidence migration</div>
        {!dry && <div className="text-[11px] text-slate-400">Run the Dry Run first — Apply unlocks after you review the counts and sample.</div>}
        {dry && (
          <>
            <div className="text-[11px] text-slate-700 mb-1">
              <strong>{t.auto}</strong> deterministic record(s) will be migrated. Sample preview:
            </div>
            <RowsTable rows={autoRows} />
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder='Type MIGRATE'
                className="px-2 py-1.5 text-xs border border-slate-300 w-36 focus:border-[#F97316] focus:outline-none"
                data-testid="rp-mig-confirm-input" />
              <button onClick={apply} disabled={!!busy || confirm !== "MIGRATE"} className={btnOrange} data-testid="rp-mig-apply-btn">
                {busy === "apply" ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} Apply Migration
              </button>
              <span className="text-[10px] text-slate-400">Pre-migration backup + migration lock + verification run automatically.</span>
            </div>
          </>
        )}
        {applyResult && (
          <div className={`mt-2 border p-2.5 ${applyResult.verification?.all_passed ? "border-emerald-300 bg-emerald-50/50" : "border-rose-300 bg-rose-50/50"}`}
            data-testid="rp-mig-apply-result">
            <div className="text-[11px] font-bold mb-1">
              Migrated {applyResult.counts?.migrated} / {applyResult.counts?.auto_planned} · Conflicts {applyResult.counts?.conflicts} · Backed up {applyResult.counts?.backed_up_docs} doc(s)
            </div>
            <div className="grid sm:grid-cols-2 gap-1">
              <Check ok={applyResult.verification?.frozen_project_pnl_identical} label={`Frozen Project P&L identical (${applyResult.verification?.frozen_projects_compared} projects)`} />
              <Check ok={applyResult.verification?.ledger_postings_unchanged} label="Ledger postings checksum unchanged" />
              <Check ok={applyResult.verification?.performance_reconcile_ok} label={`Performance ↔ frozen P&L reconciles (${applyResult.verification?.reconcile_mismatch_count} mismatches)`} />
              <Check ok={applyResult.verification?.all_passed} label="ALL VERIFICATION CHECKS" />
            </div>
            <button onClick={() => downloadBlob(`/admin/reporting-period-migration/certificate/${applyResult.id}.pdf`, "certificate.pdf")}
              className={`${btnGhost} mt-2`} data-testid="rp-mig-certificate-btn">
              <FileDown size={12} /> Migration Certificate (PDF)
            </button>
          </div>
        )}
      </div>

      {/* Stage 3 — Worklist */}
      <div className="border border-slate-200 p-3 mt-2" data-testid="rp-mig-stage3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Stage 3 · Exception worklist (manual review — never auto-updated)</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={loadWorklist} disabled={!!busy} className={btnGhost} data-testid="rp-mig-worklist-btn">
            {busy === "wl" ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />} Load Worklist
          </button>
          <button onClick={() => downloadBlob("/admin/reporting-period-migration/export.xlsx?kind=worklist", "worklist.xlsx")}
            disabled={!!busy} className={btnGhost} data-testid="rp-mig-worklist-export-btn">
            <FileDown size={12} /> Worklist Excel
          </button>
        </div>
        {wl && (
          <div className="mt-2" data-testid="rp-mig-worklist-result">
            <div className="text-[11px] text-slate-700"><strong>{wl.count}</strong> record(s) need a manual decision. Resolve them on the voucher itself (audited edit dialog / month picker).</div>
            <RowsTable rows={wl.rows} cap={12} />
          </div>
        )}
      </div>

      {/* Stage 4 — Runs, rollback, re-verify */}
      <div className="border border-slate-200 p-3 mt-2" data-testid="rp-mig-stage4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Stage 4 · Audit trail, re-verification &amp; rollback</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={loadRuns} disabled={!!busy} className={btnGhost} data-testid="rp-mig-runs-btn">
            {busy === "runs" ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />} Load Runs
          </button>
          <button onClick={reverify} disabled={!!busy} className={btnGhost} data-testid="rp-mig-verify-btn">
            {busy === "verify" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Re-verify Now
          </button>
        </div>
        {verifyRes && (
          <div className="mt-2 text-[11px]" data-testid="rp-mig-verify-result">
            <Check ok={verifyRes.ok} label={`Reconcile ${verifyRes.reconcile_ok ? "OK" : "FAILED"} · ${verifyRes.log_entries_checked} log entries checked · ${verifyRes.intact} intact · ${verifyRes.superseded_by_manual_correction} superseded by audited manual edits · ${verifyRes.flagged?.length || 0} flagged`} />
          </div>
        )}
        {runs && (
          <div className="mt-2 max-h-48 overflow-auto border border-slate-200" data-testid="rp-mig-runs-table">
            <table className="w-full text-[10px]">
              <thead className="bg-slate-100 sticky top-0">
                <tr>{["When (UTC)", "Mode", "By", "Migrated", "Verification", ""].map(h =>
                  <th key={h} className="text-left px-1.5 py-1 font-bold uppercase text-slate-500">{h}</th>)}</tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="px-1.5 py-1 font-mono">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="px-1.5 py-1">{r.mode}{r.rolled_back ? " · ROLLED BACK" : ""}</td>
                    <td className="px-1.5 py-1">{r.by}</td>
                    <td className="px-1.5 py-1 tabular">{r.mode === "apply" ? `${r.counts?.migrated ?? 0}` : "—"}</td>
                    <td className="px-1.5 py-1">
                      {r.mode === "apply" ? (r.verification?.all_passed
                        ? <span className="text-emerald-700 font-bold">PASSED</span>
                        : <span className="text-rose-700 font-bold">FAILED</span>) : "—"}
                    </td>
                    <td className="px-1.5 py-1">
                      {r.mode === "apply" && (
                        <span className="inline-flex gap-1">
                          <button onClick={() => downloadBlob(`/admin/reporting-period-migration/certificate/${r.id}.pdf`, "certificate.pdf")}
                            title="Migration Certificate" className="text-slate-500 hover:text-[#F97316]"><FileDown size={12} /></button>
                          {!r.rolled_back && (
                            <button onClick={() => rollback(r.id)} title="Rollback this run"
                              className="text-rose-500 hover:text-rose-700" data-testid={`rp-mig-rollback-${r.id}`}><Undo2 size={12} /></button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
